// "Remove machine" from the details panel (#6563, owner ruling G11) — the state half,
// split out the way `createMachineDrain` is. Remove is `POST /v1/runners/:id/decommission`
// (`decommissionBox`, `./box-lifecycle`): drain (its task back in line now), revoke, audit.
// It replaced the card menu's bare revoke (`RevokeRunnerModal`, deleted).
//
// FAIL CLOSED, the rule `lib/runnerRevoke.ts` (deleted) stated: a destructive action must never
// silently no-op, and must never be reported cleaner than it was.
//
//   - A non-2xx keeps the inline confirm open with the error; Remove can be pressed again.
//   - A 200 whose `cascade_failed` is non-null is a machine that IS removed whose access
//     tokens still need one retry. It is not a clean removal: the panel says so and offers
//     that retry, through `POST /v1/runners/:id/revoke` (`revokeRunner`), which re-runs the
//     idempotent token cascade on a revoked machine. Never a second decommission: on a
//     revoked machine that answers 404.
//   - A 200 from the retry clears the warning; a non-2xx keeps it, with the error.
//   - A second press while either call is in flight is dropped.
//
// THE STATE OUTLIVES THE ROW. The re-read after a removal drops the machine from the
// roster, so this holds the machine's id and name itself; the warning renders from here,
// not from a roster row that is gone. It outlives the PANEL too: a `cascade_failed`
// removal is parked on the Fleet page (`createMachineRemovals`, `./machine-removals`), so
// moving the panel to another machine, or closing it, never hides it.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import type { BoxDecommissioned } from './box-lifecycle';

/** What a removal needs of the machine: the id it writes and the name it says. */
export type RemovableMachine = { readonly id: string; readonly display_name: string };

export type RemoveState = {
  readonly id: string;
  readonly name: string;
  /** `confirming`: the inline confirm is up. `cascade_failed`: removed, tokens owed a retry. */
  readonly phase: 'confirming' | 'cascade_failed';
  /** The last call's refusal, shown beside the control that can try again. */
  readonly error: string | null;
};

export type MachineRemove = {
  /** The removal under way for one machine, or null. */
  readonly state: () => RemoveState | null;
  /** True while a decommission or a retry is in flight. */
  readonly busy: () => boolean;
  /** Open the inline confirm for a machine. */
  readonly ask: (machine: RemovableMachine) => void;
  /** "Keep it": shut the confirm. A cascade warning is not dismissed by it. */
  readonly cancel: () => void;
  /** Decommission the machine under confirmation. */
  readonly confirm: () => void;
  /** Retry the token cascade through revoke, after a `cascade_failed` removal. */
  readonly retry: () => void;
  /** Drop everything (the panel opened another machine). Ignored while a call is in flight. */
  readonly reset: () => void;
};

export function createMachineRemove(input: {
  readonly decommission: (id: string) => Promise<BoxDecommissioned>;
  readonly revoke: (id: string) => Promise<unknown>;
  /** The machine is removed cleanly: say so and shut the panel. */
  readonly onRemoved: (machine: RemovableMachine) => void;
  /** Any landed write: re-read the roster. */
  readonly onWritten: () => void;
  /**
   * The decommission landed but its token cascade failed: this removal now holds a
   * `cascade_failed` state and its retry. `createMachineRemovals` parks it on the page here,
   * so the warning outlives whichever machine the panel shows next.
   */
  readonly onCascadeFailed?: (machine: RemovableMachine) => void;
}): MachineRemove {
  const [state, setState] = createSignal<RemoveState | null>(null);
  const [busy, setBusy] = createSignal(false);

  const finish = (s: RemoveState): void => {
    setState(null);
    input.onRemoved({ id: s.id, display_name: s.name });
  };

  const confirm = (): void => {
    const s = state();
    if (s === null || s.phase !== 'confirming' || busy()) return;
    setBusy(true);
    setState({ ...s, error: null });
    void input
      .decommission(s.id)
      .then((answer) => {
        input.onWritten();
        if (answer.cascade_failed === null) finish(s);
        else {
          setState({ ...s, phase: 'cascade_failed', error: null });
          input.onCascadeFailed?.({ id: s.id, display_name: s.name });
        }
      })
      .catch((e: unknown) =>
        setState({
          ...s,
          phase: 'confirming',
          error: errorReason(e, t('fleet.panel.remove_error')),
        }),
      )
      .finally(() => setBusy(false));
  };

  const retry = (): void => {
    const s = state();
    if (s === null || s.phase !== 'cascade_failed' || busy()) return;
    setBusy(true);
    setState({ ...s, error: null });
    void input
      .revoke(s.id)
      .then(() => {
        input.onWritten();
        finish(s);
      })
      .catch((e: unknown) =>
        setState({ ...s, error: errorReason(e, t('fleet.panel.retry_error')) }),
      )
      .finally(() => setBusy(false));
  };

  return {
    state,
    busy,
    ask: (m) => {
      if (busy()) return;
      setState({ id: m.id, name: m.display_name, phase: 'confirming', error: null });
    },
    cancel: () => {
      if (busy() || state()?.phase !== 'confirming') return;
      setState(null);
    },
    confirm,
    retry,
    reset: () => {
      if (!busy()) setState(null);
    },
  };
}
